import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { useAlertView } from '~/app';
import { Button } from '~/components/buttons/Button/Button';
import { FormDialog } from '~/components/dialogs/FormDialog';
import {
  FormActions,
  FormLayout,
  FormSection,
  Select,
  TextArea,
  TextInput,
} from '~/components/forms';
import type { SiteBrand } from '~/config/branding';
import { requiredOptionSchema } from '~/features/types';
import { useCustomForm } from '~/hooks/useCustomForm';
import { sendSupportRequest, type SupportRequestType } from './api';
import { supportPageUrl } from './supportPageUrl';

const TAGS = CT.layout.helpMenu;

// The API rejects anything longer (SUBJECT_MAX_LENGTH / MESSAGE_MAX_LENGTH in mcv-api's
// routes/support/interfaces.py).
const SUBJECT_MAX_LENGTH = 200;
const MESSAGE_MAX_LENGTH = 5000;

// [MCV-1866] The Type dropdown. The values are the API's SUPPORT_REQUEST_TYPES.
const TYPE_OPTIONS: readonly { value: SupportRequestType; label: string }[] = [
  { value: 'ISSUE', label: 'Report an issue' },
  { value: 'QUESTION', label: 'Ask a question' },
  { value: 'FEEDBACK', label: 'Share feedback' },
];

const supportRequestSchema = z.object({
  'support-type': requiredOptionSchema('Type is required.'),
  'support-subject': z
    .string()
    .trim()
    .min(1, 'Subject is required.')
    .max(SUBJECT_MAX_LENGTH, `Subject must be at most ${SUBJECT_MAX_LENGTH} characters.`),
  'support-message': z
    .string()
    .trim()
    .min(1, 'Message is required.')
    .max(MESSAGE_MAX_LENGTH, `Message must be at most ${MESSAGE_MAX_LENGTH} characters.`),
});

type SupportRequestValues = z.infer<typeof supportRequestSchema>;

/**
 * The error toast's second line. A 429 is the API's per-user limit on support requests, and its
 * `detail` asks the user to wait, which "Please try again." would contradict.
 */
const failureSubText = (err: unknown) => {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } } | null)
    ?.response;
  if (response?.status !== 429) return 'Please try again.';
  const detail = response.data?.detail;
  return typeof detail === 'string' && detail.trim()
    ? detail
    : 'Please wait a few minutes and try again.';
};

const DEFAULT_TITLE = 'Contact support';
const DEFAULT_INTRO = 'Tell us what you need. We will reply to the email address on your account.';

interface SupportRequestFormProps {
  brand: SiteBrand;
  intro: string;
  /** Preselects the Type. Omitted, the user must pick one. */
  defaultType?: SupportRequestType;
  /** Closes the dialog. The form calls it once a request has gone out. */
  onClose: () => void;
  /** Reports whether a request is in flight, so the dialog can refuse to be dismissed meanwhile. */
  onSubmittingChange: (submitting: boolean) => void;
}

/**
 * Mounted only while the dialog is open (a closed Headless UI Dialog renders no children), so
 * every opening starts empty and the alert hook is only read once someone is sending a request.
 */
const SupportRequestForm = ({
  brand,
  intro,
  defaultType,
  onClose,
  onSubmittingChange,
}: SupportRequestFormProps) => {
  const alertView = useAlertView();
  const methods = useCustomForm<SupportRequestValues>({
    resolver: zodResolver(supportRequestSchema),
    defaultValues: {
      'support-type': TYPE_OPTIONS.find((option) => option.value === defaultType) ?? {
        value: '',
        label: '',
      },
      'support-subject': '',
      'support-message': '',
    },
  });
  const { isSubmitting } = methods.formState;

  // The dialog owns dismissal, so it has to know while a request is out. Reset on unmount so a form
  // that goes away mid-request cannot leave the dialog locked.
  useEffect(() => {
    onSubmittingChange(isSubmitting);
    return () => onSubmittingChange(false);
  }, [isSubmitting, onSubmittingChange]);

  const send = async (values: SupportRequestValues) => {
    try {
      await sendSupportRequest({
        brand,
        subject: values['support-subject'],
        message: values['support-message'],
        requestType: values['support-type'].value as SupportRequestType,
        // Where the user was, so support can see the screen: the path plus a plain tab hash or
        // `view` param. The rest of the query string stays out (invitation codes and the like).
        pageUrl: supportPageUrl(window.location),
      });
    } catch (err) {
      // Stay open with the text intact so the user can retry.
      console.error('Support request failed', err);
      alertView.send({
        type: 'ON_SHOW_ALERT',
        payload: {
          text: 'Could not send your request',
          subText: failureSubText(err),
          type: 'error',
          timeout: 5000,
        },
      });
      return;
    }
    alertView.send({
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'Request sent',
        subText: 'Our support team will reply to the email address on your account.',
        type: 'success',
        timeout: 5000,
      },
    });
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(send)}>
        <FormLayout>
          <p className="text-sm text-grey-300">{intro}</p>
          <FormSection componentsPerLine={1}>
            <Select
              name="support-type"
              label="Type"
              cyTag={TAGS.typeSelect}
              options={TYPE_OPTIONS}
              placeholder="Select a type..."
              required
            />
            <TextInput
              name="support-subject"
              label="Subject"
              cyTag={TAGS.subjectInput}
              maxLength={SUBJECT_MAX_LENGTH}
              required
            />
            <TextArea
              name="support-message"
              label="Message"
              cyTag={TAGS.messageInput}
              rows={6}
              maxLength={MESSAGE_MAX_LENGTH}
              required
            />
          </FormSection>
          <FormActions>
            <Button
              cyTag={TAGS.cancelButton}
              type="button"
              label="Cancel"
              colorVariant="secondary"
              disabled={isSubmitting}
              onClick={onClose}
            />
            <Button cyTag={TAGS.sendButton} type="submit" label="Send" disabled={isSubmitting} />
          </FormActions>
        </FormLayout>
      </form>
    </FormProvider>
  );
};

interface SupportRequestDialogProps {
  open: boolean;
  brand: SiteBrand;
  onClose: () => void;
  /** The dialog's heading. Defaults to the ? menu's "Contact support". */
  title?: string;
  /** The line above the fields. */
  intro?: string;
  /** [MCV-1866] Preselects the Type. The header's Contact support passes none, so the user picks. */
  defaultType?: SupportRequestType;
}

/**
 * The "Contact support" form from the header's ? menu: subject and message to the brand's support inbox.
 * [MCV-1866] The floating Feedback button reuses it with its own title, intro and default Type.
 *
 * While a request is in flight the dialog cannot be dismissed. Escape, the backdrop and the close
 * button all reach `dismiss`, which ignores them until the request settles (Cancel is disabled in
 * the form). Otherwise the form would unmount while its send is still running: on success it would
 * close a dialog the user had reopened, on failure the error toast would appear after the typed
 * text was gone, and a cancel-then-resend would send the request twice. The form's own `onClose`
 * is the unguarded one, because a successful send closes the dialog while still "submitting".
 */
export const SupportRequestDialog = ({
  open,
  brand,
  onClose,
  title = DEFAULT_TITLE,
  intro = DEFAULT_INTRO,
  defaultType,
}: SupportRequestDialogProps) => {
  const [submitting, setSubmitting] = useState(false);
  const dismiss = () => {
    if (!submitting) onClose();
  };

  return (
    <FormDialog creating={open} onClose={dismiss} title={title} width="lg">
      <SupportRequestForm
        brand={brand}
        intro={intro}
        defaultType={defaultType}
        onClose={onClose}
        onSubmittingChange={setSubmitting}
      />
    </FormDialog>
  );
};
